import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from "recharts";

export default function CompModal({ compsUsed, onClose, isLoggedIn, carId, mestimate, sourcePage }) {
  const navigate = useNavigate();

  useEffect(() => {
    window.gtag?.("event", "view_comparables", {
      car_id: carId,
      source_page: sourcePage,
      event_category: "engagement",
    });
    window.fbq?.("track", "ViewContent", { content_name: "Comparable Modal", content_type: "comps" });
  }, [carId, sourcePage]);

  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [onClose]);

  const handleSignupClick = () => {
    window.fbq?.("trackCustom", "JoinClick", { source: "comps_modal" });
    window.gtag?.("event", "join_click", { event_category: "engagement", event_label: "Comps Modal CTA" });
    navigate("/register");
  };

  const chartData = (compsUsed || [])
    .filter((c) => c.price)
    .map((c) => ({
      soldDate: c.soldDate ? new Date(c.soldDate) : null,
      price: Number(c.price),
      label: `${c.year} ${c.make} ${c.model}${c.trim ? ` ${c.trim}` : ""}`,
    }))
    .sort((a, b) => (a.soldDate || 0) - (b.soldDate || 0))
    .map((c, index) => ({ ...c, compLabel: `Comp ${index + 1}` }));

  return (
    <div className="fixed inset-0 z-50 bg-black/60 p-4" onClick={onClose}>
      <div
        className="mx-auto flex h-full w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="sticky top-0 z-10 border-b border-slate-200 bg-white px-4 py-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Comparable vehicles</h2>
              <p className="mt-1 text-sm text-slate-500">Sold listings that support the current estimate.</p>
            </div>
            <button
              onClick={onClose}
              className="rounded-full p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              aria-label="Close"
            >
              &times;
            </button>
          </div>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          <section className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
            {chartData.length > 0 ? (
              <>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm font-semibold text-slate-900">Sold price trend</div>
                  <div className="text-xs text-slate-600">
                    {`Comps used: ${compsUsed.length} - Range: ${priceRange(compsUsed)}`}
                  </div>
                </div>
                <div className="mt-3" style={{ width: "100%", height: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="compLabel" tick={{ fontSize: 10, fill: "#64748b" }} />
                      <YAxis
                        tick={{ fontSize: 10, fill: "#64748b" }}
                        tickFormatter={(v) => `$${Number(v).toLocaleString()}`}
                        width={52}
                      />
                      <Tooltip
                        contentStyle={{ borderRadius: 12, borderColor: "#e2e8f0" }}
                        formatter={(v, name, props) => {
                          const label = name === "price" ? props.payload.label : "Mestimate";
                          return [`$${Number(v).toLocaleString()}`, label];
                        }}
                        labelFormatter={(label) => label}
                      />
                      <Line type="monotone" dataKey="price" stroke="#2563eb" strokeWidth={2.5} dot={false} />
                      {typeof mestimate === "number" && (
                        <ReferenceLine
                          y={mestimate}
                          stroke="#ef4444"
                          strokeDasharray="4 4"
                          label={{
                            value: `Mestimate $${Number(mestimate).toLocaleString()}`,
                            position: "insideTopRight",
                            fontSize: 10,
                          }}
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </>
            ) : (
              <div className="text-sm text-slate-600">Not enough comp data to chart yet.</div>
            )}
          </section>

          {compsUsed.length > 0 ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {compsUsed.map((comp, idx) => (
                <article
                  key={idx}
                  className="flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
                >
                  <div className="relative bg-slate-100">
                    {comp.imageUrl ? (
                      <img
                        src={comp.imageUrl}
                        alt={`${comp.year} ${comp.make} ${comp.model}`}
                        className="h-44 w-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex h-44 items-center justify-center bg-slate-100 text-sm text-slate-500">
                        No image available
                      </div>
                    )}
                    <span className="absolute left-3 top-3 rounded-full bg-rose-600 px-2.5 py-1 text-[11px] font-semibold text-white shadow">
                      Sold
                    </span>
                    {comp.exactMatch && (
                      <span className="absolute right-3 top-3 rounded-full bg-emerald-600 px-2.5 py-1 text-[11px] font-semibold text-white shadow">
                        Exact match
                      </span>
                    )}
                  </div>

                  <div className="flex flex-1 flex-col gap-3 p-4">
                    <div className="min-h-[3.5rem]">
                      <h3 className="text-sm font-semibold text-slate-900">
                        {comp.year ?? "Year"} {comp.make ?? "Make"} {comp.model ?? "Model"} {comp.trim ?? ""}
                      </h3>
                      <div className="mt-1 text-sm text-slate-600">
                        {comp.mileage != null ? `${Number(comp.mileage).toLocaleString()} miles` : "Mileage unavailable"}
                      </div>
                    </div>

                    <div className="space-y-1">
                      <div className="text-2xl font-semibold text-slate-950">
                        {comp.price != null ? `$${Number(comp.price).toLocaleString()}` : "Price N/A"}
                      </div>
                      <div className="text-sm text-slate-600">
                        {comp.soldDate ? `Sold ${new Date(comp.soldDate).toLocaleDateString()}` : "Sold date unavailable"}
                      </div>
                      {/* {comp.dealerName?.trim() && (
                        <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                          <span>{comp.dealerName}</span>
                          {comp.isSameDealer && (
                            <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700 ring-1 ring-inset ring-blue-200">
                              Same dealer
                            </span>
                          )}
                        </div>
                      )} */}
                      {/* <div className="text-sm text-slate-600">
                        {comp.distanceMiles != null ? `${Number(comp.distanceMiles).toFixed(1)} miles away` : "Distance unavailable"}
                      </div> */}
                      {/* <div className="text-sm text-slate-600">
                        {comp.location || comp.dealerCity || comp.dealerState
                          ? [comp.location, comp.dealerCity, comp.dealerState].filter(Boolean).join(", ")
                          : "Location unavailable"}
                      </div> */}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 text-sm text-slate-600">
              No sold comparable cards available.
            </div>
          )}

          {!isLoggedIn && (
            <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-4 text-center">
              <div className="font-semibold text-blue-900">Want to save cars and track pricing?</div>
              <p className="mt-1 text-sm text-blue-800">
                Create a free account to keep your comparisons and leads in one place.
              </p>
              <button
                onClick={handleSignupClick}
                className="mt-3 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
              >
                Create free account
              </button>
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 bg-white px-4 py-3">
          <button
            onClick={onClose}
            className="w-full rounded-xl border border-slate-300 bg-slate-100 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function priceRange(compsUsed) {
  const nums = (compsUsed || []).map((c) => +c.price || 0).filter(Boolean);
  if (!nums.length) return "-";
  return `$${Math.min(...nums).toLocaleString()}-$${Math.max(...nums).toLocaleString()}`;
}
